// Browser-safe catalog of the Tessera Autonomy flight gig: id, label, blurb,
// the data-entry field spec, and the conformance teaching hint.
//
// Mirrors the other catalogs in shape and role, for the single gig surfaced in
// the Tessera Autonomy app. It imports NOTHING heavy — no profile/schema JSON,
// no seal path, no Node built-ins — so both the browser UI and the /api/run
// Function can import it. It is the single source of truth for the gig id and
// input shape:
//   - the UI renders the mission data-entry form from `fields`,
//   - the Tessera /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs-flight.ts (the seal-bound gig) reuses this id.
// A test asserts every flight catalog id resolves to a gig so the two cannot
// drift.
//
// The seal-side gig reads: missionRef, crossSector, peakAltitudeFt. `crossSector`
// is a closed select — "A-2"/"A-3"/"N-7" are on the mandate's allowlist; "R-1"
// (restricted) is the off-allowlist violation lever. `peakAltitudeFt` is the
// altitude on the A-2 -> crossSector crossing; over 400 ft is the altitude
// violation lever (the ceiling is enforced per leg, so even a small breach
// fails). Captured imagery is hashed into the receipt, never sealed raw.

import type { GigCatalogEntry } from "./gig-catalog.js";

export const FLIGHT_GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "flight_execution",
    label: "Aerial inspection mission",
    blurb:
      "Tessera's drone flies an infrastructure-inspection mission under a flight mandate: authorized airspace sectors and an altitude ceiling. It takes off, traverses sectors, captures the inspection targets, and recovers: one sealed flight record. Every airspace leg and every capture is checked against the mandate. The captured imagery is hashed into the receipt, never sealed raw. Enter a mission and run it: the agent seals a real Sequesign record server-side, then you verify it here in your browser.",
    usesReview: false,
    runLabel: "Seal the flight record",
    fields: [
      {
        key: "peakAltitudeFt",
        label: "Peak altitude on the A-2 → A-3 crossing (ft AGL)",
        type: "number",
        default: "382",
        min: 0,
        help: "Altitude ceiling is 400 ft AGL, enforced on every leg. Enter more than 400 (e.g. 403) to seal a record that honestly reads conformant: false."
      },
      {
        // A closed set: A-2/A-3/N-7 are on the mandate's authorized-sector
        // allowlist; "R-1" is restricted (off-allowlist) and is the second
        // violation lever. Keep in sync with ALLOWED_SECTORS in gigs-flight.ts.
        key: "crossSector",
        label: "Sector traversed & inspected",
        type: "select",
        default: "A-3",
        options: ["A-3", "N-7", "R-1"],
        help: "Authorized: A-3, N-7. “R-1” is restricted airspace, off the allowlist."
      },
      {
        key: "missionRef",
        label: "Mission reference",
        type: "text",
        default: "FLT-2026-0814-17"
      }
    ],
    violationHint:
      "Set the peak altitude over 400 ft (e.g. 403), or pick the restricted “R-1” sector, to seal a flight record that honestly records conformant: false."
  }
];

export function flightCatalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return FLIGHT_GIG_CATALOG[0];
  return FLIGHT_GIG_CATALOG.find((g) => g.id === id);
}
